import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { CheckCircleIcon } from "@heroicons/react/24/solid";

import Modal from "../../../layouts/Modal";
import LoadingWheel from "../../shared/LoadingWheel";
import SelectInput from "../../shared/Inputs/SelectInput";
import { TextInput } from "../../shared/Inputs/TextInput";
import useNotification from "../../notifications/useNotifications";

import {
  ICheckInItem,
  ICheckInStep,
} from "../../../models/CheckIn";
import { ButtonInput } from "../../shared/Inputs/ButtonInput";
import ConfirmModal from "../../shared/Confirm/ConfirmModal";
import useDefaultCRUDHandlers from "../../../hooks/useDefaultCRUDHandlers";
import useLeads from "../../../data/useLeads";

export default function LeadCheckInModal({
  open,
  setOpen,
  vehicleId,
  leadId,
  showTryCompleteLead = true,
  steps,
  items,
  isLoading,
  saveCheckInItem,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  vehicleId: string;
  leadId?: string;
  showTryCompleteLead?: boolean;
  steps: ICheckInStep[];
  items: ICheckInItem[];
  isLoading: boolean;
  saveCheckInItem: {
    mutate: (body: any, opts?: any) => void;
  };
}) {
  const { addNotification } = useNotification();
  const { saveHandlers } = useDefaultCRUDHandlers("Lead");
  const { bulkComplete } = useLeads(true, "LeadCheckInModal");

  const [savingStepId, setSavingStepId] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const checkInRows = useMemo(() => {
    return steps.map((step) => {
      const item = items.find((x) => x.checkInStepId === step.id);
      return {
        step,
        item,
        isCompleted: !!item?.completed,
      };
    });
  }, [steps, items]);

  const { register, setValue: rhfSetValue, watch, reset } = useForm<Record<string, string>>();

  const isCompletedByStepId = useMemo(() => {
    const map: Record<string, boolean> = {};
    for (const row of checkInRows) {
      map[row.step.id] = row.isCompleted;
    }
    return map;
  }, [checkInRows]);

  const parseOptions = (options: string | null) => {
    if (!options) return [];
    return options
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean);
  };

  useEffect(() => {
    const nextValues: Record<string, string> = {};
    for (const item of items) {
      nextValues[item.checkInStepId] = item.value ?? "";
    }
    reset(nextValues);
  }, [items, reset]);

  const saveValueKeepingCompletion = (stepId: string, value: string) => {
    if (!vehicleId) return;

    setSavingStepId(stepId);
    saveCheckInItem.mutate(
      {
        vehicleId,
        checkInStepId: stepId,
        value,
        isCompleted: isCompletedByStepId[stepId] ?? false,
      },
      {
        onError: () => {
          addNotification({
            variant: "error",
            primaryText: "Unable to update check-in item",
          });
        },
        onSettled: () => setSavingStepId(null),
      }
    );
  };

  const toggleCompletion = (stepId: string, nextCompleted: boolean) => {
    if (!vehicleId) return;

    const currentValue = watch(stepId) ?? "";

    setSavingStepId(stepId);
    saveCheckInItem.mutate(
      {
        vehicleId,
        checkInStepId: stepId,
        value: currentValue,
        isCompleted: nextCompleted,
      },
      {
        onError: () => {
          addNotification({
            variant: "error",
            primaryText: "Unable to update check-in completion",
          });
        },
        onSettled: () => setSavingStepId(null),
      }
    );
  };

  const registerWithSaveOnBlur = (stepId: string) =>
    register(stepId, {
      onBlur: (e) => {
        const target = e.target as HTMLInputElement;
        saveValueKeepingCompletion(stepId, target.value ?? "");
      },
    });

  const setValueSaving = (stepId: string, nextValue: any) => {
    rhfSetValue(stepId, nextValue);
    saveValueKeepingCompletion(stepId, String(nextValue ?? ""));
  };

  const handleTryToComplete = () => {
    if (!leadId) return;

    const body = {
      leads: [leadId]
    };

    bulkComplete.mutate(body, {
      ...saveHandlers,
      onSuccess: () => {
        saveHandlers.onSuccess();
        setOpen(false);
        setConfirmOpen(false);
      }
    });
  }

  return (
    <>
      {showTryCompleteLead && !!leadId && (
        <ConfirmModal open={confirmOpen} setOpen={setConfirmOpen}
          title={"Try To Complete?"}
          message={"This will raise a payment, attempt to COD the vehicle and generate an invoice."}
          confirmButtonText={"Confirm"}
          onConfirm={handleTryToComplete}
          isLoading={bulkComplete.isLoading}
        />
      )}

      <Modal open={open} setOpen={setOpen as any} width="max-w-6xl">
        <div className="space-y-4">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Check-In Tasks</h3>
              <p className="text-sm text-gray-600">Complete the remaining check-in items for this vehicle.</p>
            </div>
            <button
              type="button"
              className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
              onClick={() => setOpen(false)}
            >
              Close
            </button>
          </div>

          {isLoading ? (
            <div className="py-6">
              <LoadingWheel />
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Item</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Value</th>
                  <th className="px-4 py-2 text-center text-xs font-semibold text-gray-600">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {checkInRows.map(({ step, item, isCompleted }) => (
                  <tr key={step.id} className="bg-white hover:bg-gray-50">
                    <td className="px-4 py-2 text-sm">{step.name}</td>
                    <td className="px-4 py-2 text-sm">
                      {step.needsValue ? (
                        (() => {
                          const parsedOptions = parseOptions(step.options);
                          if (parsedOptions.length > 0) {
                            return (
                              <div className="w-full">
                                <SelectInput
                                  label=""
                                  register={register}
                                  registerName={step.id}
                                  options={parsedOptions.map((opt) => ({ id: opt, name: opt }))}
                                  setValue={setValueSaving}
                                  watch={watch}
                                  disabled={savingStepId === step.id}
                                  cols="w-full"
                                  size="sm"
                                />
                              </div>
                            );
                          }

                          return (
                            <TextInput
                              label=""
                              register={registerWithSaveOnBlur as any}
                              registerName={step.id}
                              initialValue={item?.value ?? ""}
                              disabled={savingStepId === step.id}
                              placeholder="Enter value"
                              cols="w-full"
                              size="sm"
                            />
                          );
                        })()
                      ) : (
                        <span className="text-gray-400">{item?.value || "-"}</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-center">
                      <button
                        type="button"
                        className="inline-flex items-center justify-center"
                        disabled={savingStepId === step.id}
                        onClick={() => toggleCompletion(step.id, !isCompleted)}
                      >
                        {isCompleted ? (
                          <CheckCircleIcon className="h-5 w-5 text-green-500" />
                        ) : (
                          <span className="h-5 w-5 rounded-full border border-gray-300 inline-block" />
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div className="flex justify-between">
            {showTryCompleteLead && !!leadId && (
              <ButtonInput
                label="Try to Complete Lead"
                onClick={() => setConfirmOpen(true)}
                classes={undefined}
              />
            )}

            <ButtonInput
              label="Check-In Done"
              onClick={() => setOpen(false)}
              classes={undefined}
            />
          </div>
        </div>
      </Modal>
    </>
  );
}

